import { useMemo, useState, useEffect } from 'react';
import { ADDONS, PRICING, WHATSAPP_NUMBER, BASE_PAGE_NAMES, PAGE_OPTIONS } from '../constants';
import { fmt, offerActive, nextDeadline, offerDeadlineLabel, getQuoteNo } from '../utils';

export const useBuild = () => {
  const [platform, _setPlatform] = useState('static');
  const setPlatform = (p) => {
    _setPlatform(p);
    if (p === 'wp') {
      setAddons((prev) => prev.filter((id) => {
        const addon = ADDONS.find((a) => a.id === id);
        return !addon?.notWithWp;
      }));
    }
  };
  const [pages, setPages] = useState([...BASE_PAGE_NAMES]);
  const [otherOn, setOtherOn] = useState(false);
  const [otherName, setOtherName] = useState('');
  const [addons, setAddons] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (offerActive()) {
      const ms = nextDeadline() - Date.now() + 50;
      if (ms > 0) {
        const t = setTimeout(() => setTick((n) => n + 1), ms);
        return () => clearTimeout(t);
      }
    }
  }, [tick]);

  const maxPages = PRICING.basePages + PRICING.maxExtraPages;
  const currentCount = pages.length + (otherOn ? 1 : 0);

  const togglePage = (name) => {
    if (name === 'Home') return; // Cannot remove Home
    if (pages.includes(name)) {
      setPages((p) => p.filter((n) => n !== name));
    } else {
      if (currentCount >= maxPages) return;
      setPages((p) => [...p, name]);
    }
  };

  const selectedPages = useMemo(() => {
    const list = PAGE_OPTIONS.filter((po) => pages.includes(po));
    if (otherOn) {
      list.push(otherName.trim() || 'Other page');
    }
    return list;
  }, [pages, otherOn, otherName]);

  const totalPages = selectedPages.length;
  const extraPages = Math.max(0, totalPages - PRICING.basePages);

  const toggleAddon = (id) => {
    const addon = ADDONS.find((a) => a.id === id);
    if (platform === 'wp' && addon?.notWithWp) return;
    setAddons((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };

  const lines = useMemo(() => {
    const plat = PRICING.platforms[platform];
    const items = [];
    
    items.push({ name: plat.label, sub: `${Math.min(totalPages, PRICING.basePages)} pages, logo, content`, price: plat.price, market: plat.market });
    items.push({ name: 'Domain (1 year)', sub: '.com or .in', price: 0, market: PRICING.domain, free: true });
    items.push({ name: 'Cloud hosting (1 year)', sub: 'SSL included', price: 0, market: PRICING.hosting, free: true });

    if (extraPages > 0) {
      items.push({
        name: `${extraPages} extra page${extraPages > 1 ? 's' : ''}`,
        sub: `₹${fmt(PRICING.perPage)} each`,
        price: extraPages * PRICING.perPage,
        market: extraPages * PRICING.perPageMarket,
      });
    }
    ADDONS.filter((a) => addons.includes(a.id)).forEach((a) =>
      items.push({ name: a.title, sub: a.sub || '', price: a.price, market: a.market }),
    );
    return items;
  }, [platform, extraPages, totalPages, addons]);

  const subtotal = lines.reduce((s, i) => s + i.price, 0);
  const discounts = [];
  if (platform === 'wp' && offerActive()) {
    discounts.push({ label: 'Limited offer discount', sub: 'Free WordPress upgrade, your limited-time offer', amount: PRICING.platforms.wp.price - PRICING.platforms.static.price, offer: true });
  }

  const discount = discounts.reduce((s, d) => s + d.amount, 0);
  const offerDiscount = discounts.find(d => d.offer)?.amount || 0;
  
  const net = subtotal - discount;
  const total = net;
  const marketTotal = lines.reduce((s, i) => s + (i.market || i.price), 0);
  const savings = marketTotal - net;
  const deliveryText = 'Live within 24 hours of receiving your details';
  const discountLabel = 'Limited offer discount';
  const discountSub = 'Free WordPress upgrade, your limited-time offer';

  const waHref = useMemo(() => {
    const body = lines
      .map((i) => {
        if (i.free) {
          return `• ${i.name}${i.sub ? ` (${i.sub})` : ''}: FREE (worth ₹${fmt(i.market)})`;
        }
        return `• ${i.name}${i.sub ? ` (${i.sub})` : ''}: ₹${fmt(i.price)}`;
      })
      .join('\n');
    let msg = `Hi, I'd like to order a website. Quote ${getQuoteNo()}\n\n${body}\n\nPages (${totalPages}): ${selectedPages.join(', ')}\n\n`;
    
    if (discounts.length > 0) {
      msg += `Subtotal: ₹${fmt(subtotal)}\n`;
      discounts.forEach((d) => {
        msg += `${d.label}: -₹${fmt(d.amount)}\n`;
      });
    }
    if (offerDiscount > 0) {
      msg += `Offer valid for orders sent before ${offerDeadlineLabel()}\n`;
    }
    msg += `Total: ₹${fmt(total)}\n${deliveryText}`;
    
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
  }, [lines, subtotal, discounts, offerDiscount, total, deliveryText, tick, totalPages, selectedPages]);

  return {
    platform, setPlatform,
    extraPages, totalPages, selectedPages, pages, togglePage, otherOn, setOtherOn, otherName, setOtherName,
    addons, toggleAddon,
    lines, subtotal, discount, discountLabel, discountSub, discounts, offerDiscount, net, total, marketTotal, savings, deliveryText, waHref,
  };
};
